import { CheckCircle2, ShieldCheck } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';

import { api } from '../../services/api/client';

const PLANS = [
  {
    id: 'beta',
    name: 'Beta',
    eyebrow: 'Free during beta',
    description: 'Virtual Hardhat is in beta. All features are free during this period.',
    icon: ShieldCheck,
    enabled: true,
    cta: 'Choose Beta',
  },
];

export default function PlanSelector({
  organizationId,
  selectedPlan = 'beta',
  onPlanChange,
  onPlanSelected,
  disabled = false,
}) {
  const [submittingPlan, setSubmittingPlan] = useState(null);
  const [error, setError] = useState('');

  const handleSelectPlan = useCallback(
    async (plan) => {
      setError('');
      onPlanChange?.(plan);

      if (!organizationId) {
        onPlanSelected?.({ plan });
        return;
      }

      setSubmittingPlan(plan);
      try {
        const response = await api.post(`/api/organizations/${organizationId}/select-plan`, { plan });
        onPlanSelected?.(response);
      } catch (selectError) {
        setError(selectError?.message || 'Plan selection failed. Please try again.');
      } finally {
        setSubmittingPlan(null);
      }
    },
    [organizationId, onPlanChange, onPlanSelected]
  );

  return (
    <section aria-labelledby="plan-selector-heading" className="space-y-3">
      <div className="pb-3 border-b border-black/10">
        <h2
          id="plan-selector-heading"
          className="text-sm font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-2"
        >
          <ShieldCheck className="h-4 w-4 text-[#FF6A00]" />
          Plan
        </h2>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {PLANS.map((plan) => {
          const Icon = plan.icon;
          const isSelected = selectedPlan === plan.id;
          const isBusy = submittingPlan === plan.id;
          const isDisabled = disabled || !plan.enabled || !!submittingPlan;

          return (
            <button
              key={plan.id}
              type="button"
              onClick={() => handleSelectPlan(plan.id)}
              disabled={isDisabled}
              aria-pressed={isSelected}
              className={`min-h-[190px] rounded-xl border p-4 text-left transition-all duration-200 ${
                isSelected
                  ? 'border-[#FF6A00]/50 bg-[#FF6A00]/10 shadow-md'
                  : 'border-black/10 bg-black/[0.03]'
              } ${
                plan.enabled
                  ? 'hover:border-[#FF6A00]/40 hover:bg-black/[0.05]'
                  : 'cursor-not-allowed opacity-55'
              }`}
            >
              <div className="flex h-full flex-col gap-3">
                <div className="flex items-start justify-between gap-3">
                  <div className={`rounded-lg p-2 ${isSelected ? 'bg-orange-500/25' : 'bg-black/[0.05]'}`}>
                    <Icon className={`h-5 w-5 ${isSelected ? 'text-[#E25C00]' : 'text-slate-600'}`} />
                  </div>
                  {isSelected && <CheckCircle2 className="h-5 w-5 text-[#E25C00]" aria-hidden="true" />}
                </div>
                <div className="space-y-1">
                  <p className="text-base font-semibold text-[#1C1C1E]">{plan.name}</p>
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#E25C00]">{plan.eyebrow}</p>
                </div>
                <p className="flex-1 text-sm leading-5 text-slate-600">{plan.description}</p>
                <span
                  className={`inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold ${
                    plan.enabled
                      ? 'bg-orange-600 text-white'
                      : 'bg-black/[0.06] text-slate-500'
                  }`}
                >
                  {isBusy ? 'Selecting...' : plan.cta}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {error && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
          {error}
        </p>
      )}
    </section>
  );
}

PlanSelector.propTypes = {
  organizationId: PropTypes.string,
  selectedPlan: PropTypes.oneOf(['beta']),
  onPlanChange: PropTypes.func,
  onPlanSelected: PropTypes.func,
  disabled: PropTypes.bool,
};
